import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, Plus, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { FormDialog } from '@/components/ui/form-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import toast from 'react-hot-toast';
import type { MenuModifier } from '@/types/restaurant';

interface OptionDraft { name: string; priceAdjust: number; }

interface SaveModifierPayload {
  name: string;
  required: boolean;
  multiSelect: boolean;
  minSelect: number;
  maxSelect: number | undefined;
  sortOrder: number;
  options: { name: string; priceAdjust: number; sortOrder: number }[];
}

const emptyForm = {
  name: '',
  required: false,
  multiSelect: false,
  minSelect: 0,
  maxSelect: undefined as number | undefined,
  sortOrder: 0,
  options: [{ name: '', priceAdjust: 0 }] as OptionDraft[],
};

export function MenuModifiersPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModifier, setEditingModifier] = useState<MenuModifier | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<MenuModifier | null>(null);
  const [deleteOptionTarget, setDeleteOptionTarget] = useState<{ modifierId: string; optionId: string; name: string } | null>(null);

  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');

  const { data: modifiers, isLoading } = useQuery<MenuModifier[]>({
    queryKey: ['menu-modifiers'],
    queryFn: () => api.get('/menu-modifiers').then((r) => r.data?.data ?? r.data ?? []),
  });

  const saveMutation = useMutation({
    mutationFn: (data: SaveModifierPayload) =>
      editingModifier
        ? api.patch(`/menu-modifiers/${editingModifier.id}`, data)
        : api.post('/menu-modifiers', data),
    onSuccess: () => {
      toast.success(editingModifier ? t('modifierUpdated') : t('modifierCreated'));
      queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] });
      setDialogOpen(false);
      setEditingModifier(null);
      setForm(emptyForm);
    },
    onError: () => toast.error(t('failedToSaveModifier')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/menu-modifiers/${id}`),
    onSuccess: () => {
      toast.success(t('modifierDeleted'));
      queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] });
      setDeleteTarget(null);
    },
    onError: () => toast.error(t('failedToDeleteModifier')),
  });

  const deleteOptionMutation = useMutation({
    mutationFn: ({ modifierId, optionId }: { modifierId: string; optionId: string }) =>
      api.delete(`/menu-modifiers/${modifierId}/options/${optionId}`),
    onSuccess: () => {
      toast.success(t('optionDeleted'));
      queryClient.invalidateQueries({ queryKey: ['menu-modifiers'] });
      setDeleteOptionTarget(null);
    },
    onError: () => toast.error(t('failedToDeleteOption')),
  });

  const openEdit = (mod: MenuModifier) => {
    setEditingModifier(mod);
    setForm({
      name: mod.name,
      required: mod.required,
      multiSelect: mod.multiSelect,
      minSelect: mod.minSelect,
      maxSelect: mod.maxSelect,
      sortOrder: mod.sortOrder,
      options: mod.options.map((o) => ({ name: o.name, priceAdjust: o.priceAdjust })),
    });
    setDialogOpen(true);
  };

  const addOption = () => setForm({ ...form, options: [...form.options, { name: '', priceAdjust: 0 }] });
  const removeOption = (i: number) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });
  const updateOption = (i: number, field: keyof OptionDraft, val: string | number) =>
    setForm({ ...form, options: form.options.map((o, idx) => idx === i ? { ...o, [field]: val } : o) });

  const handleSave = () => {
    if (!form.name) { toast.error(t('modifierNameRequired')); return; }
    if (form.options.some((o) => !o.name)) { toast.error(t('allOptionsNeedName')); return; }
    saveMutation.mutate({
      name: form.name,
      required: form.required,
      multiSelect: form.multiSelect,
      minSelect: Number(form.minSelect),
      maxSelect: form.maxSelect ? Number(form.maxSelect) : undefined,
      sortOrder: Number(form.sortOrder),
      options: form.options.map((o, idx) => ({ name: o.name, priceAdjust: Number(o.priceAdjust), sortOrder: idx })),
    });
  };

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('menuModifiers')}
        subtitle={t('modifiersSubtitle')}
        action={
          canWrite ? (
            <Button onClick={() => { setEditingModifier(null); setForm(emptyForm); setDialogOpen(true); }}>
              <Plus className="me-2 h-4 w-4" />
              Add Modifier
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : !modifiers?.length ? (
        <EmptyState
          icon={SlidersHorizontal}
          title={t('noModifiersYet')}
          description={t('noModifiersHint')}
          action={canWrite ? { label: t('addModifier'), onClick: () => { setEditingModifier(null); setForm(emptyForm); setDialogOpen(true); } } : undefined}
        />
      ) : (
        <div className="space-y-3">
          {modifiers.map((mod) => (
            <Card key={mod.id} className="overflow-hidden">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => setExpandedId(expandedId === mod.id ? null : mod.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{mod.name}</span>
                      {mod.required && (
                        <span className="rounded-full bg-destructive/10 text-destructive-text px-2 py-0.5 text-xs font-medium">
                          {t('required')}
                        </span>
                      )}
                      {mod.multiSelect && (
                        <span className="rounded-full bg-info/10 text-info-text px-2 py-0.5 text-xs font-medium">
                          {t('multiSelect')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {mod.options.slice(0, 5).map((opt) => (
                        <span key={opt.id} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                          {opt.name}{opt.priceAdjust > 0 ? ` +${formatCurrency(opt.priceAdjust)}` : ''}
                        </span>
                      ))}
                      {mod.options.length > 5 && (
                        <span className="text-xs text-muted-foreground">+{mod.options.length - 5} more</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ms-3" onClick={(e) => e.stopPropagation()}>
                  {canWrite && (
                    <TableRowActions
                      actions={[
                        {
                          key: 'edit',
                          label: t('editModifier'),
                          icon: Pencil,
                          onSelect: () => openEdit(mod),
                          primary: true,
                        },
                        {
                          key: 'delete',
                          label: t('deleteModifierTitle'),
                          icon: Trash2,
                          onSelect: () => setDeleteTarget(mod),
                          variant: 'destructive',
                        },
                      ]}
                    />
                  )}
                  {expandedId === mod.id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </div>

              {expandedId === mod.id && (
                <div className="border-t px-4 pb-4">
                  <p className="text-xs text-muted-foreground mt-3 mb-2 font-medium uppercase tracking-wide">{t('options')} ({mod.options.length})</p>
                  <div className="space-y-1.5">
                    {mod.options.map((opt) => (
                      <div key={opt.id} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{opt.name}</span>
                          {opt.priceAdjust !== 0 && (
                            <span className="text-xs text-muted-foreground">
                              {opt.priceAdjust > 0 ? '+' : ''}{formatCurrency(opt.priceAdjust)}
                            </span>
                          )}
                          {!opt.isActive && (
                            <span className="rounded-full bg-muted text-muted-foreground px-1.5 py-0.5 text-xs">{t('inactive')}</span>
                          )}
                        </div>
                        {canWrite && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive-text h-6 px-2"
                            onClick={() => setDeleteOptionTarget({ modifierId: mod.id, optionId: opt.id, name: opt.name })}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <FormDialog
        open={dialogOpen}
        title={editingModifier ? t('editModifier') : t('addModifier')}
        width="50rem"
        submitLabel={t('saveModifier')}
        cancelLabel={t('cancel')}
        isPending={saveMutation.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('modifierName')} <span className="text-destructive-text">*</span></Label>
            <Input placeholder={t('modifierNamePlaceholder')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('sortOrder')}</Label>
            <Input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <Label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
            <span className="text-sm font-medium">{t('required')}</span>
          </Label>
          <Label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox checked={form.multiSelect} onChange={(e) => setForm({ ...form, multiSelect: e.target.checked })} />
            <span className="text-sm font-medium">{t('multiSelect')}</span>
          </Label>
        </div>

        {form.multiSelect && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">{t('minSelections')}</Label>
              <Input type="number" min="0" value={form.minSelect} onChange={(e) => setForm({ ...form, minSelect: Number(e.target.value) })} />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('maxSelectionsOptional')}</Label>
              <Input type="number" min="1" value={form.maxSelect ?? ''} onChange={(e) => setForm({ ...form, maxSelect: e.target.value ? Number(e.target.value) : undefined })} placeholder={t('unlimited')} />
            </div>
          </div>
        )}

        {/* Options */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="font-semibold">{t('options')}</Label>
            <Button type="button" size="sm" variant="outline" onClick={addOption}>
              <Plus className="me-1 h-3.5 w-3.5" />{t('addOption')}
            </Button>
          </div>
          <div className="space-y-2">
            {form.options.map((opt, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2">
                <Input
                  className="flex-1"
                  placeholder={t('optionNamePlaceholder')}
                  value={opt.name}
                  onChange={(e) => updateOption(i, 'name', e.target.value)}
                />
                <Input
                  className="w-full sm:w-32"
                  type="number"
                  step="0.01"
                  placeholder={t('priceAdjPlaceholder')}
                  value={opt.priceAdjust}
                  onChange={(e) => updateOption(i, 'priceAdjust', Number(e.target.value))}
                />
                {form.options.length > 1 && (
                  <Button type="button" size="sm" variant="outline" className="text-destructive-text flex-shrink-0 self-start sm:self-center" onClick={() => removeOption(i)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('deleteModifierTitle')}
        message={`Delete modifier "${deleteTarget?.name}" and all its options?`}
        confirmLabel={t('delete')}
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteOptionTarget}
        title={t('deleteOptionTitle')}
        message={`Delete option "${deleteOptionTarget?.name}"?`}
        confirmLabel={t('delete')}
        variant="danger"
        loading={deleteOptionMutation.isPending}
        onConfirm={() => deleteOptionTarget && deleteOptionMutation.mutate(deleteOptionTarget)}
        onCancel={() => setDeleteOptionTarget(null)}
      />
    </div>
  );
}
